import React, { FC, MouseEvent, useEffect, useState } from 'react';
import { TableCell, TableRow, Typography, useMediaQuery, Theme, Box, IconButton, Tooltip } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import { InvoicingState, Order, OrderDeliveryStatus, OrderTableType } from 'models/order';
import { formatDate } from 'utils/format';
import RestorationTypes, { RESTORATION_TYPES_CELL_SX } from 'components/orders/shared/restoration-types.component';
import DeliveryAndDeadlineDetails from 'components/orders/shared/delivery-and-deadline-details.component';
import OrderPrice from 'components/orders/shared/order-price.component';
import OrderItemIconArea from 'components/orders/shared/order-item-icon-area/order-item-icon-area.component';
import OrderStatusChip from 'components/orders/shared/order-status-chip.component';
import LabFlagButton from 'components/shared/lab-flag-button/lab-flag-button.component';
import OrderListTagComponent from 'components/shared/order-list-tag/order-list-tag.component';
import OrderCheckBox from 'components/shared/order-check-box/order-check-box.component';
import TimeInStep from 'components/shared/time-in-step/time-in-step.component';
import { useTranslation } from 'react-i18next';
import { OrderItemContext, OrderItemEvent, OrderItemEventType } from 'models/order-item-events';
import { useNavigate } from 'react-router-dom';
import { OrderTag } from 'models/tags';
import { isSupplier } from 'utils/client-settings';
import { formatPatientName } from 'utils/format/format-patient';
import { isSupplierOrder } from 'utils/orders';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { OrderListColumnKey } from './column-registry';
import { pausedLabel } from 'utils/paused-label';

type OrderListRowProps = {
    tableType: OrderTableType;
    order: Order;
    onOrderContextMenu: (orderContext: OrderItemContext) => void;
    onOrderItemEvent: (event: OrderItemEvent) => void;
    batchActionSelected?: boolean;
    onHandleCheckedOrders?: (order: Order, checked: boolean, orderIndex: number) => void;
    orderIndex: number;
    activeColumns: OrderListColumnKey[];
    showIconArea?: boolean;
    isAtLimit?: boolean;
    isChecked?: boolean;
};

const OrderListRow: FC<OrderListRowProps> = ({
    tableType,
    order,
    onOrderContextMenu,
    onOrderItemEvent,
    batchActionSelected,
    onHandleCheckedOrders,
    orderIndex,
    activeColumns,
    showIconArea = true,
    isAtLimit,
    isChecked,
}: OrderListRowProps) => {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
    const { t: translate } = useTranslation(['orders', 'workflow', 'invoices', 'milling-jobs']);
    const navigate = useNavigate();
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();
    const [orderTags, setOrderTags] = useState<OrderTag[]>();
    const shouldDisplayDelayed = currentUser?.viewSettings?.deliveryDatePassed || false;

    useEffect(() => {
        setOrderTags(order.orderTags);
    }, [order.orderTags]);

    const navigateToOrderDetails = () => {
        navigate(`/order-details/${order._id}/main-view`);
    };

    const handleContextMenu = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        onOrderContextMenu({ order: order, mouseX: event.clientX, mouseY: event.clientY });
    };

    const colorMap = {
        [InvoicingState.SHOULD_NOT_INVOICE]: '',
        [InvoicingState.READY_FOR_INVOICE]: 'success.main',
        [InvoicingState.INVOICED]: 'success.main',
    };

    const updateOrderTag = (tags: OrderTag[]) => {
        setOrderTags(tags);
    };

    const getCustomerLabel = () => {
        if (isSupplierOrder(order)) {
            return tableType === OrderTableType.SUPPLIER ? order.supplier?.name : order.customer?.name;
        }
        // Clinic orders show the delivery clinic (pickup location), as before DD-3481.
        return order.deliveryDetails?.pickUpLocation?.name;
    };

    const cellRenderers: Record<OrderListColumnKey, () => React.ReactNode> = {
        status: () => (
            <TableCell sx={{ maxWidth: '10vw' }}>
                <OrderStatusChip status={order.status} />
                {order.invoicingState && order.invoicingState !== InvoicingState.NOT_READY && !isMobile ? (
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                color: colorMap[order.invoicingState],
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {translate(`invoices:${order.invoicingState}`)}
                        </Typography>
                    </Box>
                ) : order.isProductionFinished && !isMobile ? (
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'disabled.main',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {translate('PRODUCTION_FINISHED')}
                        </Typography>
                    </Box>
                ) : undefined}
            </TableCell>
        ),
        orderId: () => (
            <TableCell>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.orderId}</Box>
                {order.internalOrderId ? (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {order.internalOrderId}
                    </Typography>
                ) : undefined}
            </TableCell>
        ),
        orderIdOnly: () => (
            <TableCell>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.orderId}</Box>
            </TableCell>
        ),
        internalOrderId: () => (
            <TableCell>
                {order.internalOrderId ? (
                    <Typography
                        variant="body2"
                        sx={{
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {order.internalOrderId}
                    </Typography>
                ) : (
                    <span>-</span>
                )}
            </TableCell>
        ),
        patient: () => (
            <TableCell sx={{ wordBreak: 'break-word' }} className="mouseflow-mask">
                {/* DD-3494: a patient-less order shows the placeholder, not a blank name and "No PIN". */}
                <Box>{formatPatientName(order.patient, translate('orders:NO_PATIENT_REFERENCE'))}</Box>
                {order.patient && !order.patient.personalIdentityNumber ? (
                    <Typography variant="body2" color="grey.700">
                        {translate('orders:NO_PIN_NUMBER')}
                    </Typography>
                ) : undefined}
                {order.patient?.personalIdentityNumber ? (
                    <Typography variant="caption">{order.patient.personalIdentityNumber}</Typography>
                ) : undefined}
            </TableCell>
        ),
        constructions: () => (
            <TableCell sx={RESTORATION_TYPES_CELL_SX}>
                <RestorationTypes sampleType={order.deliveryDetails?.sampleType} types={order.restorationTypes} />
            </TableCell>
        ),
        customer: () => (
            <TableCell sx={{ maxWidth: 300, minWidth: 200 }}>
                <Box>{getCustomerLabel()}</Box>
                <Typography variant="caption">{order.dentist?.fullName}</Typography>
            </TableCell>
        ),
        price: () => (
            <TableCell
                sx={{
                    '& .MuiTypography-root': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    },
                }}
            >
                <OrderPrice price={order.deliveryDetails?.price} orderId={order._id} currency={order.currency} />
            </TableCell>
        ),
        created: () => (
            <TableCell sx={{ minWidth: 160, maxWidth: 175 }}>
                {order.created?.on ? <div>{formatDate(new Date(order.created.on), 'datetime')}</div> : null}
                {order.created?.by?.fullName && order.created.by.fullName !== order.dentist?.fullName ? (
                    <div>
                        <Typography variant="caption">{order.created.by.fullName}</Typography>
                    </div>
                ) : undefined}
            </TableCell>
        ),
        deliveryDeadline: () => (
            <TableCell sx={{ width: 115 }}>
                <DeliveryAndDeadlineDetails
                    details={order.deliveryDetails}
                    showDeadline={Boolean(
                        order.supplier?._id && isSupplier(currentOrganization._id, order.supplier._id),
                    )}
                />
            </TableCell>
        ),
        orderTag: () => (
            <TableCell>
                {orderTags?.length && !isMobile ? (
                    <OrderListTagComponent updateOrderTag={updateOrderTag} orderId={order._id} orderTags={orderTags} />
                ) : (
                    <span>-</span>
                )}
            </TableCell>
        ),
        timeInStep: () => (
            <TableCell>
                <TimeInStep
                    timeSpent={order.workFlow?.timeInCurrentStep}
                    maxHourInStep={order.workFlow?.currentStep?.maxHourInStep}
                />
                {order.workFlow?.currentStep?.dateTime && (
                    <Typography variant="caption" component="div" color="grey.500">
                        {formatDate(new Date(order.workFlow.currentStep.dateTime), 'datetime')}
                    </Typography>
                )}
                {order.workFlow?.currentStep?.isPaused ? (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'warning.main',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                        title={pausedLabel(order.workFlow?.currentStep?.pauseReason, translate)}
                    >
                        {pausedLabel(order.workFlow?.currentStep?.pauseReason, translate)}
                    </Typography>
                ) : undefined}
            </TableCell>
        ),
        workflow: () => (
            <TableCell>
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {(() => {
                        const stepName = order.workFlow?.currentStep?.name;
                        return !stepName || stepName === 'NO_STEP' || stepName === 'No Step'
                            ? translate('workflow:NO_STEP')
                            : stepName;
                    })()}
                </Box>
                {order.workFlow?.currentStep?.workStarted && order.workFlow?.currentStep?.user?.fullName ? (
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {order.workFlow.currentStep.user.fullName}
                    </Typography>
                ) : undefined}
            </TableCell>
        ),
        workStatus: () => (
            <TableCell>
                <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                    <CircleIcon
                        sx={{
                            fontSize: 10,
                            color: order.workFlow?.currentStep?.workStarted ? 'success.main' : '#fbc02d',
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="body2"
                        color={order.workFlow?.currentStep?.workStarted ? 'textPrimary' : 'textSecondary'}
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                        }}
                    >
                        {order.workFlow?.currentStep?.workStarted
                            ? order.workFlow?.currentStep?.user?.fullName || '-'
                            : translate('workflow:WORK_NOT_STARTED')}
                    </Typography>
                </Box>
            </TableCell>
        ),
        materials: () => (
            <TableCell sx={{ maxWidth: 200 }}>
                {order.constructionMaterials?.map((material, index) => <Box key={index}>{material}</Box>)}
            </TableCell>
        ),
        delivery: () => (
            <TableCell>
                {order.deliveryDetails?.date &&
                order.deliveryDetails?.status === OrderDeliveryStatus.DELAYED &&
                shouldDisplayDelayed ? (
                    <Tooltip title={translate('orders:DELIVERY_STATUS_DELAYED').toString()}>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                            {formatDate(new Date(order.deliveryDetails.date), 'date')}
                        </Typography>
                    </Tooltip>
                ) : order.deliveryDetails?.date ? (
                    formatDate(new Date(order.deliveryDetails.date), 'date')
                ) : (
                    <span>-</span>
                )}
            </TableCell>
        ),
        internalDeliveryDate: () => (
            <TableCell>
                {order.deliveryDetails?.internalDeliveryDate &&
                order.deliveryDetails?.deadlineStatus === OrderDeliveryStatus.DELAYED ? (
                    <Tooltip title={translate('orders:DELIVERY_STATUS_DELAYED').toString()}>
                        <Typography variant="body2" sx={{ color: 'error.main' }}>
                            {formatDate(order.deliveryDetails.internalDeliveryDate, 'date')}
                        </Typography>
                    </Tooltip>
                ) : order.deliveryDetails?.internalDeliveryDate ? (
                    formatDate(order.deliveryDetails.internalDeliveryDate, 'date')
                ) : (
                    translate('milling-jobs:NO_DEADLINE')
                )}
            </TableCell>
        ),
        externalDeliveryDate: () => (
            <TableCell>
                {order.deliveryDetails?.externalDeliveryDate ? (
                    <Box>{formatDate(new Date(order.deliveryDetails.externalDeliveryDate), 'date')}</Box>
                ) : (
                    <span>-</span>
                )}
            </TableCell>
        ),
        sentToCustomer: () => (
            <TableCell>
                {order.manualSelectedSentDate ? (
                    <Box>{formatDate(new Date(order.manualSelectedSentDate), 'date')}</Box>
                ) : (
                    <span>{translate('orders:NOT_SENT_YET')}</span>
                )}
            </TableCell>
        ),
        panNumber: () => (
            <TableCell>
                <Box display="flex" alignItems="center" gap={0.5}>
                    {order.panNumber ? (
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {order.panNumber}
                            </Box>
                            {order.panNumberDescription ? (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {order.panNumberDescription}
                                </Typography>
                            ) : undefined}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="grey.700">
                            {translate('orders:NOT_SET')}
                        </Typography>
                    )}
                    {/* This cell only renders when the PAN feature is enabled (gated by the
                        column filter in order-list-table.component.tsx), so the edit action is
                        always safe to show here. */}
                    <Tooltip title={translate('orders:EDIT_PAN_NUMBER').toString()}>
                        <IconButton
                            size="small"
                            color="primary"
                            sx={{ padding: 0 }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onOrderItemEvent({ order, eventType: OrderItemEventType.MANAGE_PAN_NUMBER });
                            }}
                            data-test="order-list-row.edit-pan-number-btn"
                        >
                            <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </TableCell>
        ),
    };

    return (
        <TableRow
            sx={{ cursor: batchActionSelected ? 'default' : 'pointer' }}
            hover
            onClick={() => {
                if (!batchActionSelected) {
                    navigateToOrderDetails();
                }
            }}
            onContextMenu={handleContextMenu}
        >
            {batchActionSelected ? (
                <TableCell sx={{ padding: 0 }}>
                    <OrderCheckBox
                        order={order}
                        isAtLimit={isAtLimit}
                        checked={isChecked ?? false}
                        onHandleCheckedOrders={
                            onHandleCheckedOrders &&
                            ((checkedOrder, checked) => onHandleCheckedOrders(checkedOrder, checked, orderIndex))
                        }
                    />
                </TableCell>
            ) : (
                <TableCell sx={{ padding: 0 }}>
                    {order.supplier?._id && isSupplier(currentOrganization._id, order.supplier._id) ? (
                        <Box>
                            <LabFlagButton order={order} />
                        </Box>
                    ) : undefined}
                </TableCell>
            )}

            {activeColumns.map((columnKey) => (
                <React.Fragment key={columnKey}>{cellRenderers[columnKey]()}</React.Fragment>
            ))}

            <TableCell />

            {showIconArea ? (
                <TableCell sx={{ paddingRight: 0.5, paddingLeft: 0.5 }} align="right">
                    <OrderItemIconArea order={order} onOrderItemEvent={onOrderItemEvent} />
                </TableCell>
            ) : undefined}
            <TableCell sx={{ paddingLeft: 0, paddingRight: 0.5 }} align="right">
                <IconButton size="small" onClick={handleContextMenu}>
                    <MoreVertIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default OrderListRow;
